import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { Observable, forkJoin, of, Subscription } from 'rxjs';
import { catchError, finalize, map, skip } from 'rxjs/operators';
import { ConsentAuthorizationStep } from '../../../models/wizard/consent-authorization-step-model';
import { CreditService } from '../../../services/credit.service';
import { WizardStepComponentBase } from '../wizard-step-base.component';
import { Borrower } from '../../../models/borrower-model';
import { formatDate } from '@angular/common';
import { FeatureFlags } from '../../../models/feature-flags.model';
import { localizeField } from '../../../transloco/localize-field';
import { ErrorMessage } from '../../../models/error-message.model';
import { getErrorMessageOrDefault } from 'projects/shared/utils/error.utils';

interface ConsentCallOutcome {
  label: string;
  error?: unknown;
}

declare const $: any;

@Component({
    selector: 'demographics',
    templateUrl: 'consent-authorization-step.component.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConsentAuthorizationStepComponent extends WizardStepComponentBase<ConsentAuthorizationStep> implements OnInit, OnDestroy {

  showEConsentVerbiage: boolean;
  showCreditAuthVerbiage: boolean;

  creditCheckConsentVerbiage: string | undefined = "";
  eBusinessConsentVerbiage: string | undefined = "";

  mainBorrower!: Borrower;
  coBorrowers: Borrower[] = [];

  creditCheckConsentAlreadyGiven: boolean = false;
  hasAlreadyValidEConsent: boolean = false;

  creditCheckConsentGiven: boolean = false;
  eConsentGiven: boolean = false;

  private _eConsentGenerationEnabled: boolean = false;
  private _creditAuthGenerationEnabled: boolean;
  private _enableCreditAuthTaskWorkflow: boolean = false;
  private _config: FeatureFlags | undefined;
  private _langSub?: Subscription;
  private readonly _recordedConsents = new Set<string>();

  get isStepComplete(): boolean {
    return this.eConsentGiven && (!this.showCreditAuthVerbiage || (this.showCreditAuthVerbiage
      && this.creditCheckConsentGiven));
  }

  get canMoveToNextStep(): boolean {
    return this.isStepComplete;
  }

  constructor(private readonly _injector: Injector,
    private readonly _creditService: CreditService,
    private readonly _cdr: ChangeDetectorRef) {
    super(_injector);
    this.saveMortgageApplicationBeforeNextStep = true;
    const borrowers = this.inCoApplyFlow ? [this.currentBorrower] : this.mortgageApplication.borrowers;
    if (this.currentBorrower) {
      this.mainBorrower = this.currentBorrower;
      this.creditCheckConsentAlreadyGiven = this.mainBorrower.authorizedCreditCheck || false;
      this.hasAlreadyValidEConsent = this.mainBorrower.hasValidEConsent || false;
      this.eConsentGiven = this.mainBorrower.hasValidEConsent || false;
      this.creditCheckConsentGiven = this.mainBorrower.authorizedCreditCheck || false;

      this.coBorrowers = borrowers.filter(b => b.borrowerId !== this.mainBorrower.borrowerId);
      this.coBorrowers.forEach(c => {
        c.willBeEdited = this.borrowerSetting(c);
      });
    }
  }

  ngOnInit() {
    this.startSpinner();
    this.mortgageApplicationService.getConfig(this.mortgageApplication.applicationId).pipe(
      finalize(() => {
        this.stopSpinner();
        this._cdr.markForCheck();
      })
    )
      .subscribe(response => {
        this._config = response;
        this.refreshConsentVerbiage();
        this._eConsentGenerationEnabled = response.eConsentGenerationEnabled;
        this.showEConsentVerbiage = response.showEConsentVerbiage;
        this.showCreditAuthVerbiage = response.showCreditAuthVerbiage;
        this._creditAuthGenerationEnabled = response.creditAuthGenerationEnabled;
        this._enableCreditAuthTaskWorkflow = response.enableCreditAuthTaskWorkflow || false;
        this._cdr.markForCheck();
      });
    this._langSub = this.translocoService.langChanges$.pipe(skip(1)).subscribe(() => {
      this.refreshConsentVerbiage();
      this._cdr.markForCheck();
    });
  }

  private refreshConsentVerbiage(): void {
    const lang = this.translocoService.getActiveLang();
    this.creditCheckConsentVerbiage = localizeField(this._config, 'creditAuthText', lang);
    this.eBusinessConsentVerbiage = localizeField(this._config, 'eConsentText', lang);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._langSub?.unsubscribe();
  }

  onNextClicked() {

    const apiCalls: Observable<ConsentCallOutcome>[] = [];

    if (this._creditAuthGenerationEnabled && this.creditCheckConsentGiven && !this.creditCheckConsentAlreadyGiven) {

      const today = formatDate(new Date(), 'MM/dd/yyyy', 'en-US');
      this.mainBorrower.dateAuthorizedCreditCheck = today;
      this.mainBorrower.authorizationMethod = "Internet";
      this.mainBorrower.authorizedCreditCheck = true;

      this.coBorrowers.forEach(coBorrower => {
        if (this.borrowerSetting(coBorrower)) {
          coBorrower.authorizedCreditCheck = true;
          coBorrower.dateAuthorizedCreditCheck = today;
          coBorrower.authorizationMethod = "Internet";
        }
      });

      this.push(apiCalls,
        'creditAuth',
        this.translocoService.translate('wizard.consent.creditAuthorization'),
        () => this._creditService.createConsentReport(this.mortgageApplication.applicationId, this.creditCheckConsentVerbiage));
    }

    if (this._eConsentGenerationEnabled && this.eConsentGiven && !this.hasAlreadyValidEConsent) {
      this.push(apiCalls,
        `eConsent-${this.mainBorrower.contactId}`,
        this.borrowerLabel(this.mainBorrower),
        () => this.mortgageApplicationService.generateEConsentDocument(this.mortgageApplication.applicationId,
          { eConsentText: this.eBusinessConsentVerbiage, borrowerId: this.mainBorrower.contactId }));
      if (this.coBorrowers && this.coBorrowers.length > 0) {
        this.coBorrowers.forEach(coBorrower => {
          if (this.borrowerSetting(coBorrower)) {
            this.push(apiCalls,
              `eConsent-${coBorrower.contactId}`,
              this.borrowerLabel(coBorrower),
              () => this.mortgageApplicationService.generateEConsentDocument(this.mortgageApplication.applicationId,
                { eConsentText: this.eBusinessConsentVerbiage, borrowerId: coBorrower.contactId }));
          }
        });
      }
    }

    if (apiCalls.length === 0) {
      super.onNextClicked();
      return;
    }

    this.startSpinner();
    forkJoin(apiCalls).subscribe(outcomes => {
      this.stopSpinner();

      const failures = outcomes.filter(outcome => outcome.error !== undefined);
      if (failures.length === 0) {
        super.onNextClicked();
        return;
      }

      this.showError(new ErrorMessage(
        this.translocoService.translate('wizard.consent.errorRecordingConsentTitle'),
        failures
          .map(failure => `${failure.label}: ${getErrorMessageOrDefault(failure.error, {
            defaultMessage: this.translocoService.translate('wizard.consent.errorRecordingConsentMessage')
          })}`)
          .join('\n')));
      this._cdr.markForCheck();
      window.scroll(0, 0);
    });
  }

  /**
   * Queues a consent call unless it already succeeded on an earlier attempt, so retrying the step
   * re-sends only the calls that failed. Each call reports its own outcome rather than throwing:
   * forkJoin fails fast, so an unhandled error would cancel and discard every sibling call.
   */
  private push(apiCalls: Observable<ConsentCallOutcome>[], key: string, label: string, call: () => Observable<unknown>): void {
    if (this._recordedConsents.has(key)) {
      return;
    }
    apiCalls.push(call().pipe(
      map(() => {
        this._recordedConsents.add(key);
        return { label } as ConsentCallOutcome;
      }),
      catchError((error: unknown) => of({ label, error: error ?? new Error(label) }))));
  }

  private borrowerLabel(borrower: Borrower): string {
    const name = [borrower.firstName, borrower.lastName].filter(part => !!part).join(' ').trim();
    const eConsent = this.translocoService.translate('wizard.consent.eConsent');
    return name ? `${eConsent} (${name})` : eConsent;
  }
}
